import { useEffect, useRef } from 'react';

function getNumber(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function getPreviewOrderLabel(value) {
  switch (value) {
    case 'COLUMN_BY_COLUMN':
      return 'Coluna por coluna';
    case 'ROW_BY_ROW':
      return 'Linha por linha';
    case 'LEFT_TO_RIGHT':
      return 'Esquerda para direita';
    case 'TOP_TO_BOTTOM':
      return 'Cima para baixo';
    default:
      return 'Fluxo padrío';
  }
}

export default function LabelFormatCanvas({ model, className = '', variant = 'default' }) {
  const canvasRef = useRef(null);
  const payload = model?.payload ?? {};
  const isThumbnail = variant === 'thumbnail';
  const layoutMode = payload.layoutMode || model?.layoutMode || 'CONTINUOUS';
  const legendColumns = model?.columns || getNumber(payload.columns, 1);
  const legendRows = Math.max(1, Math.trunc(getNumber(payload.rows, model?.rows ?? 1)));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const ratio = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 320;
    const cssHeight = canvas.clientHeight || (isThumbnail ? 86 : 220);
    canvas.width = Math.round(cssWidth * ratio);
    canvas.height = Math.round(cssHeight * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const widthMm = Math.max(1, getNumber(payload.columnWidthMm ?? payload.widthMm, model?.columnWidthMm ?? model?.widthMm ?? 100));
    const heightMm = Math.max(1, getNumber(payload.columnHeightMm ?? payload.heightMm, model?.columnHeightMm ?? model?.heightMm ?? 50));
    const columns = Math.max(1, Math.trunc(getNumber(payload.columns, model?.columns ?? 1)));
    const rowsConfigured = Math.max(1, Math.trunc(getNumber(payload.rows, model?.rows ?? 1)));
    const previewRows = Math.max(1, Math.trunc(getNumber(payload.previewRows, model?.previewRows ?? 4)));
    const leftMarginMm = Math.max(0, getNumber(payload.leftMarginMm, 0));
    const topMarginMm = Math.max(0, getNumber(payload.topMarginMm, 0));
    const rightMarginMm = Math.max(0, getNumber(payload.rightMarginMm, model?.rightMarginMm ?? 0));
    const bottomMarginMm = Math.max(0, getNumber(payload.bottomMarginMm, model?.bottomMarginMm ?? 0));
    const pageWidthMm = Math.max(0, getNumber(payload.paperWidthMm ?? payload.pageWidthMm, model?.paperWidthMm ?? model?.pageWidthMm ?? 0));
    const pageHeightMm = Math.max(0, getNumber(payload.paperHeightMm ?? payload.pageHeightMm, model?.paperHeightMm ?? model?.pageHeightMm ?? 0));
    const horizontalGapMm = Math.max(0, getNumber(payload.horizontalGapMm, 0));
    const verticalGapMm = Math.max(0, getNumber(payload.verticalGapMm, 0));
    const previewOrder = payload.previewOrder || 'ROW_BY_ROW';
    const rows = layoutMode === 'PAGE'
      ? rowsConfigured
      : (isThumbnail ? Math.min(2, Math.max(1, rowsConfigured)) : Math.min(6, Math.max(1, previewRows)));

    const contentWidthMm = leftMarginMm + columns * widthMm + Math.max(0, columns - 1) * horizontalGapMm + rightMarginMm;
    const contentHeightMm = topMarginMm + rows * heightMm + Math.max(0, rows - 1) * verticalGapMm + bottomMarginMm;
    const mediaWidthMm = pageWidthMm > 0 ? Math.max(pageWidthMm, contentWidthMm) : contentWidthMm;
    const mediaHeightMm = pageHeightMm > 0 ? Math.max(pageHeightMm, contentHeightMm) : contentHeightMm;

    const paddingX = isThumbnail ? 12 : 18;
    const paddingY = isThumbnail ? 12 : 16;
    const mediaAvailableWidth = cssWidth - paddingX * 2;
    const mediaAvailableHeight = cssHeight - paddingY * 2;
    const scale = Math.min(mediaAvailableWidth / mediaWidthMm, mediaAvailableHeight / mediaHeightMm);
    const mediaWidthPx = mediaWidthMm * scale;
    const mediaHeightPx = mediaHeightMm * scale;
    const offsetX = (cssWidth - mediaWidthPx) / 2;
    const offsetY = (cssHeight - mediaHeightPx) / 2;

    context.clearRect(0, 0, cssWidth, cssHeight);

    const backdrop = context.createLinearGradient(0, 0, cssWidth, cssHeight);
    backdrop.addColorStop(0, '#f7f5f1');
    backdrop.addColorStop(1, '#ece7de');
    context.fillStyle = backdrop;
    context.fillRect(0, 0, cssWidth, cssHeight);

    context.fillStyle = '#e8edf3';
    context.fillRect(offsetX, offsetY, mediaWidthPx, mediaHeightPx);
    context.strokeStyle = 'rgba(148, 163, 184, 0.42)';
    context.lineWidth = 1;
    context.strokeRect(offsetX, offsetY, mediaWidthPx, mediaHeightPx);

    const contentX = offsetX + leftMarginMm * scale;
    const contentY = offsetY + topMarginMm * scale;
    const contentBoxWidth = (columns * widthMm + Math.max(0, columns - 1) * horizontalGapMm) * scale;
    const contentBoxHeight = (rows * heightMm + Math.max(0, rows - 1) * verticalGapMm) * scale;
    context.fillStyle = '#f1f5f9';
    context.fillRect(contentX, contentY, contentBoxWidth, contentBoxHeight);

    if (layoutMode === 'CONTINUOUS') {
      const railWidth = Math.max(8, mediaWidthPx * 0.035);
      const railGradient = context.createLinearGradient(offsetX + mediaWidthPx - railWidth, offsetY, offsetX + mediaWidthPx, offsetY);
      railGradient.addColorStop(0, '#1f2937');
      railGradient.addColorStop(1, '#05070b');
      context.fillStyle = railGradient;
      context.fillRect(
        offsetX + mediaWidthPx - railWidth - 4,
        offsetY + (isThumbnail ? 5 : 8),
        railWidth,
        mediaHeightPx - (isThumbnail ? 10 : 16),
      );
    } else {
    }

    context.font = `600 ${isThumbnail ? 8 : 10}px Figtree, sans-serif`;
    context.textBaseline = 'middle';

    let slot = 1;
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        const x = offsetX + (leftMarginMm + column * (widthMm + horizontalGapMm)) * scale;
        const y = offsetY + (topMarginMm + row * (heightMm + verticalGapMm)) * scale;
        const w = widthMm * scale;
        const h = heightMm * scale;

        context.fillStyle = 'rgba(255, 255, 255, 0.96)';
        context.fillRect(x, y, w, h);
        context.strokeStyle = 'rgba(100, 116, 139, 0.55)';
        context.lineWidth = 1;
        context.strokeRect(x, y, w, h);

        context.fillStyle = 'rgba(100, 116, 139, 0.9)';
        context.fillText(`${slot}`, x + (isThumbnail ? 6 : 10), y + (isThumbnail ? 8 : 12));

        slot += 1;
      }
    }

    if (!isThumbnail) {
      context.fillStyle = '#0f172a';
      context.font = '700 11px Poppins, sans-serif';
      context.fillText(`${widthMm} x ${heightMm} mm`, offsetX + 14, offsetY + mediaHeightPx - 14);

      context.fillStyle = 'rgba(71, 85, 105, 0.95)';
      context.font = '600 10px Figtree, sans-serif';
      context.textAlign = 'right';
      context.fillText(
        `${layoutMode === 'PAGE' ? `${rows} x ${columns}` : `${previewRows} linhas`} • ${getPreviewOrderLabel(previewOrder)}`,
        offsetX + mediaWidthPx - 14,
        offsetY + mediaHeightPx - 14,
      );
      context.textAlign = 'start';
    }

    return undefined;
  }, [isThumbnail, model, payload]);

  return (
    <div className={`rounded-[20px] border border-surface-container-high bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] ${isThumbnail ? 'p-2.5' : 'p-3'} ${className}`}>
      {!isThumbnail ? (
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">Prévia de impressão</div>
        </div>
      ) : null}
      <canvas ref={canvasRef} className={`block w-full rounded-[16px] ${isThumbnail ? 'h-[86px]' : 'h-[220px]'}`} />
      {isThumbnail ? (
        <div className="mt-1 flex items-center justify-between px-1 text-[8px] font-semibold uppercase tracking-[0.14em] text-on-surface-variant">
          <span>{model?.columnWidthMm || model?.widthMm || getNumber(payload.columnWidthMm ?? payload.widthMm, 100)} x {model?.columnHeightMm || model?.heightMm || getNumber(payload.columnHeightMm ?? payload.heightMm, 50)} mm</span>
          <span>{layoutMode === 'PAGE' ? `${legendRows}x${legendColumns}` : `${legendRows} ${legendRows === 1 ? 'linha' : 'linhas'}`}</span>
        </div>
      ) : null}
    </div>
  );
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}
